/**
 * A short chime to accompany a desktop notification (DOP-274 round 2).
 *
 * The OS notification sound is the PRIMARY channel — on macOS it is the
 * per-app "Play sound for notifications" switch under System Settings →
 * Notifications → Google Chrome, and we cannot set it from a web page. This
 * chime is the in-browser guarantee: if the tab is alive at all, the user gets
 * something audible even when Chrome's own sound is off.
 *
 * Synthesised with the Web Audio API rather than shipped as an asset — two
 * short sine tones under a soft envelope, ~300ms end to end, low gain. No file
 * to load, nothing to cache, nothing to 404.
 *
 * Best-effort throughout: no Web Audio, an autoplay policy that blocks sound
 * before the first user gesture, or a context that refuses to resume all end as
 * silence. A completion sound is never worth an exception.
 */

import { isNotificationSoundEnabled } from "./notificationPreferences";

/** ~300ms total: two tones, the second starting as the first fades. */
const TONE_ONE_HZ = 880;
const TONE_TWO_HZ = 1174.66;
const TONE_SECONDS = 0.18;
const SECOND_TONE_OFFSET_SECONDS = 0.12;
/** Deliberately quiet — this plays while the user is in another app. */
const PEAK_GAIN = 0.06;

type AudioContextCtor = typeof AudioContext;

/**
 * One context for the tab's lifetime. Browsers cap how many can exist, and a
 * new one per notification would eventually stop producing sound at all.
 */
let sharedContext: AudioContext | null = null;

const audioContextCtor = (): AudioContextCtor | null => {
	if (typeof window === "undefined") return null;
	const scope = window as Window & {
		AudioContext?: AudioContextCtor;
		webkitAudioContext?: AudioContextCtor;
	};
	return scope.AudioContext ?? scope.webkitAudioContext ?? null;
};

const getContext = (): AudioContext | null => {
	if (sharedContext) return sharedContext;
	const Ctor = audioContextCtor();
	if (!Ctor) return null;
	sharedContext = new Ctor();
	return sharedContext;
};

const playTone = (
	context: AudioContext,
	frequency: number,
	startAt: number,
): void => {
	const oscillator = context.createOscillator();
	const gain = context.createGain();
	oscillator.type = "sine";
	oscillator.frequency.value = frequency;
	// A linear attack and exponential decay: a square-edged gain change is an
	// audible click, which is exactly the wrong texture for a background chime.
	gain.gain.setValueAtTime(0.0001, startAt);
	gain.gain.linearRampToValueAtTime(PEAK_GAIN, startAt + 0.02);
	gain.gain.exponentialRampToValueAtTime(0.0001, startAt + TONE_SECONDS);
	oscillator.connect(gain);
	gain.connect(context.destination);
	oscillator.start(startAt);
	oscillator.stop(startAt + TONE_SECONDS);
};

/**
 * Play the chime, unless the user turned sound off. Never throws.
 *
 * Returns whether a tone was actually scheduled, which is what the tests
 * assert on — the caller has nothing to do with the answer.
 */
export function playNotificationChime(): boolean {
	if (!isNotificationSoundEnabled()) return false;
	try {
		const context = getContext();
		if (!context) return false;
		// Suspended is the normal state before the page's first user gesture.
		// Resuming is asynchronous and may be refused; scheduling anyway costs
		// nothing and succeeds the moment the context wakes.
		if (context.state === "suspended") {
			void Promise.resolve(context.resume?.()).catch(() => undefined);
		}
		const now = context.currentTime;
		playTone(context, TONE_ONE_HZ, now);
		playTone(context, TONE_TWO_HZ, now + SECOND_TONE_OFFSET_SECONDS);
		return true;
	} catch {
		// No Web Audio, a blocked context, or a node the browser refused to
		// create. Silence is the correct outcome.
		return false;
	}
}

/** Test seam: forget the cached context so each test starts from nothing. */
export function resetNotificationChimeForTests(): void {
	sharedContext = null;
}
